/**
 * @file Helper for export proxy
 */
import { isAppUsing, isAppUsingBanksyLocalDev } from '@@/bits/flip'
import { asciiSafeStringify } from '@@/bits/json_stringify'
import { transformUrl } from '@@/bits/location'
import type { VersionedFetchOptions } from '@@/surface/api/fetchable_object'
import { WallReadTokenApi } from '@@/surface/api/wall_read_token'
import type { WallReadTokenResponse } from '@@/types'
import type { JsonAPIResource } from '@padlet/arvo'
import type { FetchOptions } from '@padlet/fetch'
import { fetchBlob, HTTPMethod } from '@padlet/fetch'

const EXPORT_PROXY_HOST = 'https://cgi.padletcdn.com'
const EXPORT_PROXY_IMAGE_EXPORT_URL = `${EXPORT_PROXY_HOST}/export/image`
const EXPORT_PROXY_PDF_EXPORT_URL = `${EXPORT_PROXY_HOST}/export/pdf`
const BANKSY_IMAGE_EXPORT_HOST = 'https://banksy-img.padletcdn.com'
const BANKSY_IMAGE_EXPORT_URL = `${BANKSY_IMAGE_EXPORT_HOST}/screenshot`
const BANKSY_PDF_EXPORT_URL = 'https://banksy.padletcdn.com/export/pdf'
const BANKSY_DEV_PDF_EXPORT_URL = 'https://banksy-dev-w3b4ywad5a-uc.a.run.app/export/pdf'
const BANKSY_LOCAL_DEV_PDF_EXPORT_URL = 'http://localhost:8888/export/pdf'
const EXPORT_PROXY_IMAGES_TO_ZIP_EXPORT_URL = `${EXPORT_PROXY_HOST}/export/images/zip`
const EXPORT_PROXY_IMAGES_TO_PDF_EXPORT_URL = `${EXPORT_PROXY_HOST}/export/images/pdf`
const CONVERT_PROXY_IMAGE_TO_PDF_URL = `${EXPORT_PROXY_HOST}/convert/image/pdf`

// Allows private padlets to be temporarily accessed
const fetchExportReadToken = async (publicKey: string, fetchOptions: VersionedFetchOptions): Promise<string> => {
  const wallReadTokenResponse = await WallReadTokenApi.fetchWallReadToken(publicKey, fetchOptions)
  const wallReadTokenData = wallReadTokenResponse.data as JsonAPIResource<WallReadTokenResponse>
  return wallReadTokenData.attributes.token
}

const stringifySearchParams = (searchParams: Partial<ImageExportParams>): Record<keyof ImageExportParams, string> => {
  const stringifiedSearchParams = {}
  Object.keys(searchParams).forEach((key) => {
    stringifiedSearchParams[key] = searchParams[key].toString()
  })
  return stringifiedSearchParams as Record<keyof ImageExportParams, string>
}

// Reference: https://github.com/padlet/mozart/blob/18e2eeacaa11ed5b72e4af012a0d9dae01657aea/serverless/workers/src/export_proxy.ts#L85
interface ImageExportParams {
  url: string
  width: number
  width_from: string
  full_width: boolean
  height: number
  retina: boolean
  full_page: boolean
  filename: string
  file_type: string
  delay: number
  timeout: number
  ttl: number
  force: boolean
  service: 'microlink' | 'urlbox'
  allow_infinite: boolean
  detect_full_height: boolean
  wait_for_selector: string
  download: boolean
}

// Reference: https://github.com/padlet/mozart/blob/18e2eeacaa11ed5b72e4af012a0d9dae01657aea/serverless/workers/src/export_proxy.ts#L481
type PdfPageSize = 'letter' | 'a4' | 'legal'
type PdfOrientation = 'landscape' | 'portrait'
interface PdfExportParams {
  url: string
  page_size: PdfPageSize
  orientation: PdfOrientation
  margin_top: number
  margin_bottom: number
  margin_left: number
  margin_right: number
  filename: string
  delay: number
  timeout: number
  force: boolean
}

const buildImageExportProxyUrl = (searchParams: Partial<ImageExportParams>): string => {
  const imageExportUrl = isAppUsing('currentSlideAsImageWithBanksy')
    ? BANKSY_IMAGE_EXPORT_URL
    : EXPORT_PROXY_IMAGE_EXPORT_URL
  return transformUrl(imageExportUrl, {
    search: stringifySearchParams(searchParams),
  })
}

const buildPdfExportProxyUrl = (searchParams: Partial<PdfExportParams>): string => {
  let pdfExportUrl = EXPORT_PROXY_PDF_EXPORT_URL
  if (isAppUsing('banksy')) {
    pdfExportUrl = BANKSY_PDF_EXPORT_URL
  } else if (isAppUsing('banksyDev')) {
    pdfExportUrl = BANKSY_DEV_PDF_EXPORT_URL
    if (searchParams.filename !== undefined) {
      searchParams.filename = '(dev) '.concat(searchParams.filename)
    }
  } else if (isAppUsingBanksyLocalDev) {
    // isAppUsingBanksyLocalDev returns true if on padlet.dev.
    // If on padlet.dev, use the local Banksy server to test PDF exports.
    // Banksy and Banksy Dev on Cloud Run won't work because they cannot access a padlet.dev padlet.
    // You will need to run the Banksy server first before you can test PDF exports.
    // See: /services/banksy/README.md
    pdfExportUrl = BANKSY_LOCAL_DEV_PDF_EXPORT_URL
    if (searchParams.filename !== undefined) {
      searchParams.filename = '(localhost) '.concat(searchParams.filename)
    }
  }

  return transformUrl(pdfExportUrl, {
    search: stringifySearchParams(searchParams),
  })
}

const buildImagesToZipExportProxyUrl = (searchParams: Partial<ImageExportParams>): string => {
  return transformUrl(EXPORT_PROXY_IMAGES_TO_ZIP_EXPORT_URL, {
    search: stringifySearchParams(searchParams),
  })
}

interface ImagesToZipPageData {
  url: string
  file_name: string
  delay: number
}
const fetchImagesToZipBlob = async (
  pageData: ImagesToZipPageData[],
  exportProxyUrl: string,
  options?: FetchOptions,
): Promise<Blob> => {
  const body = { pages: pageData }

  return await fetchBlob(exportProxyUrl, {
    method: HTTPMethod.post,
    body: asciiSafeStringify(body),
    cors: true,
    ...options,
  })
}

const buildImagesToPdfExportProxyUrl = (searchParams: Partial<ImageExportParams>): string => {
  return transformUrl(EXPORT_PROXY_IMAGES_TO_PDF_EXPORT_URL, {
    search: stringifySearchParams(searchParams),
  })
}

// Reference: https://github.com/Hopding/pdf-lib/blob/b8a44bd24b74f4f32456e9809dc4d2d9dc9bf176/src/api/sizes.ts#L8
const PDF_EXPORT_A4_LANDSCAPE_WIDTH = 841.89
const PDF_EXPORT_A4_LANDSCAPE_HEIGHT = 595.28
const PDF_EXPORT_A4_LANDSCAPE_ASPECT_RATIO = PDF_EXPORT_A4_LANDSCAPE_WIDTH / PDF_EXPORT_A4_LANDSCAPE_HEIGHT
const PDF_EXPORT_A4_PORTRAIT_WIDTH = 595.28
const PDF_EXPORT_A4_PORTRAIT_HEIGHT = 841.89

interface ImagesToPdfPageData {
  url: string
  delay: number
}

interface FetchImagesToPdfBlobBodyPayload {
  pages: ImagesToPdfPageData[]
  page_width: number
  page_height: number
}

const fetchImagesToPdfBlob = async (
  pageData: ImagesToPdfPageData[],
  pageWidth: number,
  pageHeight: number,
  exportProxyUrl: string,
  options?: FetchOptions,
): Promise<Blob> => {
  const body: FetchImagesToPdfBlobBodyPayload = { pages: pageData, page_width: pageWidth, page_height: pageHeight }

  return await fetchBlob(exportProxyUrl, {
    method: HTTPMethod.post,
    body: asciiSafeStringify(body),
    cors: true,
    ...options,
  })
}

const makeExportRequest = async (
  exportCallback: (
    wallReadTokenAbortController: AbortController,
    exportProxyAbortController: AbortController,
  ) => Promise<string | null>,
): Promise<string | null> => {
  const wallReadTokenAbortController = new AbortController()
  const exportProxyAbortController = new AbortController()

  try {
    // Intentional return await so promise rejection can be caught
    return await exportCallback(wallReadTokenAbortController, exportProxyAbortController)
  } catch (err) {
    if (wallReadTokenAbortController.signal.aborted || exportProxyAbortController.signal.aborted) {
      // User cancelled manually, do nothing
      return null
    }

    throw err
  }
}

export {
  buildImageExportProxyUrl,
  buildImagesToPdfExportProxyUrl,
  buildImagesToZipExportProxyUrl,
  buildPdfExportProxyUrl,
  CONVERT_PROXY_IMAGE_TO_PDF_URL,
  EXPORT_PROXY_HOST,
  EXPORT_PROXY_IMAGE_EXPORT_URL,
  EXPORT_PROXY_IMAGES_TO_ZIP_EXPORT_URL,
  fetchExportReadToken,
  fetchImagesToPdfBlob,
  fetchImagesToZipBlob,
  makeExportRequest,
  PDF_EXPORT_A4_LANDSCAPE_ASPECT_RATIO,
  PDF_EXPORT_A4_LANDSCAPE_HEIGHT,
  PDF_EXPORT_A4_LANDSCAPE_WIDTH,
  PDF_EXPORT_A4_PORTRAIT_HEIGHT,
  PDF_EXPORT_A4_PORTRAIT_WIDTH,
}
export type {
  FetchImagesToPdfBlobBodyPayload,
  ImageExportParams,
  ImagesToPdfPageData,
  ImagesToZipPageData,
  PdfExportParams,
  PdfOrientation,
  PdfPageSize,
}
